import { useState, useEffect, useCallback, useMemo } from 'react';
import { pruefungenAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  Award, Users, UserPlus, Download, RefreshCw, Search, ExternalLink, XCircle, Loader2, AlertTriangle, RotateCcw,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import MitgliedSuche from '@/components/MitgliedSuche';

// Gürtelprüfung (28.10.2026, backend/pruefungsanmeldung.py): Anmeldungen von der Seite /bmt-pruefung und vom Empfang.
// Einheiten und Gürtel kommen live aus der Graduierung; die Gebühr ist ein Einmalposten, frühestens auf der Rechnung nach dem Anmeldeschluss.

const QUELLE = { anmeldeseite: 'Anmeldeseite', empfang: 'Empfang' };
const POSTEN = {
  angelegt: { text: 'auf der Rechnung', variant: 'secondary' },
  anspruch: { text: 'unklar — an der Mitgliedschaft prüfen', variant: 'destructive' },
  fehler: { text: 'fehlgeschlagen', variant: 'destructive' },
  storniert: { text: 'zurückgenommen', variant: 'outline' },
  bleibt_faellig: { text: 'bleibt fällig', variant: 'outline' },
  erstatten: { text: 'per Gutschrift erstatten', variant: 'destructive' },
  keiner: { text: 'keiner', variant: 'outline' },
};

function fmtZeit(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

function monatText(m) {
  if (!m) return '';
  const [j, mo] = m.split('-');
  return `${mo}/${j}`;
}

export default function PruefungenPage() {
  const { userPermissions, isAdminPermissions } = useAuth();
  const darfVerwalten = isAdminPermissions || !!userPermissions?.['seminare.verwalten'];

  const [daten, setDaten] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suche, setSuche] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [dialog, setDialog] = useState(null);       // Prüfung, für die am Empfang angemeldet wird
  const [person, setPerson] = useState(null);
  const [groesse, setGroesse] = useState('');         // T-Shirt-Größe am Empfang (Pflicht)
  const [speichert, setSpeichert] = useState(false);
  const [absage, setAbsage] = useState(null);       // { a, p, grund }

  const laden = useCallback(async (still = false) => {
    if (!still) setLoading(true);
    try {
      const res = await pruefungenAPI.uebersicht();
      setDaten(res.data);
    } catch {
      toast.error('Anmeldungen konnten nicht geladen werden.');
    }
    setLoading(false);
  }, []);
  useEffect(() => { laden(); }, [laden]);

  const pruefungen = useMemo(() => daten?.pruefungen || [], [daten]);

  const filtern = useCallback((liste) => {
    const q = suche.trim().toLowerCase();
    return q ? liste.filter((a) => (a.name || '').toLowerCase().includes(q)) : liste;
  }, [suche]);

  const anmelden = async () => {
    if (!person) { toast.error('Bitte eine Person auswählen.'); return; }
    if (!groesse) { toast.error('Bitte wähle die T-Shirt-Größe.'); return; }
    setSpeichert(true);
    try {
      const r = await pruefungenAPI.anmelden(dialog.id, person.id, groesse);
      const po = r.data?.anmeldung?.posten;
      toast.success(`${person.name} ist angemeldet${po?.status === 'angelegt' ? ` — Gebühr auf der Rechnung ${monatText(po.monat)}` : ''}.`);
      if (po?.status === 'fehler') toast.error(`Gebühr nicht angelegt: ${po.fehler}`);
      setDialog(null); setPerson(null); setGroesse('');
      await laden(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Anmeldung fehlgeschlagen.');
    }
    setSpeichert(false);
  };

  const absagenSenden = async () => {
    const { a } = absage;
    setBusyId(a.id);
    try {
      const r = await pruefungenAPI.absagen(a.id, absage.grund || '');
      const st = r.data?.posten?.status;
      if (st === 'storniert') toast.success('Abgesagt — die Gebühr ist zurückgenommen.');
      else toast.success('Abgesagt.');
      if (r.data?.hinweis) (st === 'erstatten' ? toast.warning : toast.message)(r.data.hinweis);
      setAbsage(null);
      await laden(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Absage fehlgeschlagen.');
    }
    setBusyId(null);
  };

  const postenNachholen = async (a) => {
    setBusyId(a.id);
    try {
      const r = await pruefungenAPI.postenNachholen(a.id);
      if (r.data?.ok) toast.success('Gebühr steht jetzt auf der Rechnung.');
      else toast.error(`Wieder nicht geklappt: ${r.data?.posten?.fehler || ''}`);
      await laden(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Nachholen fehlgeschlagen.');
    }
    setBusyId(null);
  };

  const exportieren = async () => {
    try {
      const res = await pruefungenAPI.exportCSV();
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8' }));
      const el = document.createElement('a');
      el.href = url; el.download = 'pruefungsanmeldungen.csv'; el.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export fehlgeschlagen.');
    }
  };

  const summe = useMemo(() => pruefungen.reduce((s, p) => ({
    angemeldet: s.angemeldet + p.angemeldet, fehlen: s.fehlen + p.einheiten_fehlen, posten: s.posten + p.posten_offen,
  }), { angemeldet: 0, fehlen: 0, posten: 0 }), [pruefungen]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Lädt …
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Award className="h-6 w-6 text-marke" /> Gürtelprüfung
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Verbindliche Anmeldungen — von der Anmeldeseite und vom Empfang. Ob geprüft wird, entscheidet der Trainer.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => laden(true)}>
            <RefreshCw className="h-4 w-4 mr-1.5" /> Aktualisieren
          </Button>
          <Button variant="outline" size="sm" onClick={exportieren}>
            <Download className="h-4 w-4 mr-1.5" /> CSV
          </Button>
          <Button variant="outline" size="sm" asChild>
            <a href="/bmt-pruefung" target="_blank" rel="noreferrer">
              <ExternalLink className="h-4 w-4 mr-1.5" /> Anmeldeseite
            </a>
          </Button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Angemeldet</CardTitle></CardHeader>
          <CardContent><div className="text-3xl font-bold">{summe.angemeldet}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Einheiten fehlen</CardTitle></CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{summe.fehlen}</div>
            <p className="text-xs text-muted-foreground mt-1">Stand heute — der Trainer entscheidet</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Gebühr offen</CardTitle></CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{summe.posten}</div>
            <p className="text-xs text-muted-foreground mt-1">{summe.posten ? 'Nicht auf der Rechnung — bitte prüfen' : 'Alle Gebühren auf der Rechnung'}</p>
          </CardContent>
        </Card>
      </div>

      {pruefungen.map((p) => {
        const liste = filtern(p.anmeldungen || []);
        return (
          <Card key={p.id}>
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
              <div>
                <CardTitle className="text-lg">{p.titel}</CardTitle>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {p.datum_text} · {p.uhrzeit ? `${p.uhrzeit} Uhr` : 'Uhrzeit folgt'} · {p.preis_text} · Anmeldeschluss {p.anmeldeschluss_text}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {p.anmeldung_offen ? <Badge variant="secondary">Anmeldung offen</Badge> : <Badge variant="outline">Anmeldeschluss vorbei</Badge>}
                {p.abgesagt > 0 && <Badge variant="outline">{p.abgesagt} abgesagt</Badge>}
                {darfVerwalten && (
                  <Button size="sm" onClick={() => { setDialog(p); setPerson(null); setGroesse(''); }}>
                    <UserPlus className="h-4 w-4 mr-1.5" /> Anmelden
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {(p.tshirts || []).length > 0 && (
                <p className="text-sm mb-3" data-testid="tshirt-summe">
                  <span className="font-medium">T-Shirts:</span>{' '}
                  {p.tshirts.map((t) => `${t.anzahl}× ${t.groesse || 'ohne Angabe'}`).join(', ')}
                </p>
              )}
              {(p.anmeldungen || []).length > 5 && (
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input className="pl-9" placeholder="Nach Name suchen …" value={suche} onChange={(e) => setSuche(e.target.value)} />
                </div>
              )}
              {liste.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground">
                  <Users className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  {(p.anmeldungen || []).length === 0 ? 'Noch keine Anmeldungen.' : 'Kein Treffer.'}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-muted-foreground">
                        <th className="py-2 pr-3 font-medium">Name</th>
                        <th className="py-2 pr-3 font-medium">Gürtel → angestrebt</th>
                        <th className="py-2 pr-3 font-medium">T-Shirt</th>
                        <th className="py-2 pr-3 font-medium">Einheiten</th>
                        <th className="py-2 pr-3 font-medium">Gebühr</th>
                        <th className="py-2 pr-3 font-medium">Über</th>
                        <th className="py-2 pr-3 font-medium">Angemeldet</th>
                        <th className="py-2 font-medium text-right">Aktion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {liste.map((a) => {
                        const abgesagt = a.status === 'abgesagt';
                        const akt = a.aktuell;
                        const e = akt?.einheiten || a.einheiten || {};
                        const fehlt = akt ? akt.einheiten_fehlen : a.einheiten_fehlen;
                        const po = POSTEN[a.posten?.status] || { text: a.posten?.status || '—', variant: 'outline' };
                        return (
                          <tr key={a.id} className={`border-b last:border-0 align-top ${abgesagt ? 'opacity-55' : ''}`}>
                            <td className="py-2.5 pr-3 font-medium">
                              <a href={`/leads/${a.member_id}`} className="text-marke hover:underline">{a.name}</a>
                              {abgesagt && <Badge variant="outline" className="ml-2 text-xs">abgesagt</Badge>}
                              {abgesagt && a.absage_hinweis && <p className="text-xs text-muted-foreground font-normal mt-0.5">{a.absage_hinweis}</p>}
                            </td>
                            <td className="py-2.5 pr-3 whitespace-nowrap">
                              {akt?.guertel || a.guertel?.label} → <span className="font-medium">{a.ziel?.label}</span>
                            </td>
                            <td className="py-2.5 pr-3 whitespace-nowrap">{a.tshirt_groesse || '—'}</td>
                            <td className="py-2.5 pr-3 whitespace-nowrap">
                              {e.insgesamt}{e.noetig != null ? ` / ${e.noetig}` : ''}
                              {!abgesagt && fehlt && (
                                <span className="ml-2 inline-flex items-center gap-1 text-amber-600 text-xs" title="Der Trainer entscheidet">
                                  <AlertTriangle className="h-3.5 w-3.5" /> Einheiten fehlen ({e.fehlen})
                                </span>
                              )}
                            </td>
                            <td className="py-2.5 pr-3">
                              <Badge variant={po.variant}>{po.text}</Badge>
                              {a.posten?.monat && <span className="ml-1.5 text-xs text-muted-foreground">{monatText(a.posten.monat)}</span>}
                              {a.posten?.status === 'fehler' && a.posten.fehler && <p className="text-xs text-muted-foreground mt-0.5">{a.posten.fehler}</p>}
                            </td>
                            <td className="py-2.5 pr-3 text-muted-foreground">
                              {QUELLE[a.quelle] || a.quelle}{a.erfasst_von_name ? ` (${a.erfasst_von_name})` : ''}
                            </td>
                            <td className="py-2.5 pr-3 text-muted-foreground whitespace-nowrap">{fmtZeit(a.erstellt_am)}</td>
                            <td className="py-2.5 text-right whitespace-nowrap">
                              {darfVerwalten && !abgesagt && a.posten?.status === 'fehler' && (
                                <Button variant="ghost" size="sm" disabled={busyId === a.id} onClick={() => postenNachholen(a)}>
                                  <RotateCcw className="h-4 w-4 mr-1.5" /> Gebühr nachholen
                                </Button>
                              )}
                              {darfVerwalten && !abgesagt && (
                                <Button variant="ghost" size="sm" disabled={busyId === a.id}
                                        onClick={() => setAbsage({ a, p, grund: '' })}
                                        className="text-muted-foreground hover:text-destructive">
                                  <XCircle className="h-4 w-4 mr-1.5" /> Absagen
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {/* Anmeldung am Empfang */}
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Anmelden — {dialog?.titel}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">
            Verbindlich: Die Gebühr von {dialog?.preis_text} kommt auf die nächste Rechnung. Kostenlos absagen bis {dialog?.anmeldeschluss_text}.
          </p>
          <div className="space-y-2 mt-2">
            <Label>Mitglied</Label>
            <MitgliedSuche wert={person} onWahl={setPerson} placeholder="Name suchen …" tid="pruefung-person" />
            <p className="text-xs text-muted-foreground">Alle Mitglieder mit laufender Mitgliedschaft, ohne Altersgrenze. Gürtel und Einheiten kommen aus der Graduierung.</p>
          </div>
          <div className="space-y-2 mt-3">
            <Label id="pruefung-groesse-label">T-Shirt-Größe (Pflicht)</Label>
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-labelledby="pruefung-groesse-label">
              {(dialog?.tshirt_groessen || []).map((g) => (
                <Button key={g} type="button" role="radio" aria-checked={groesse === g}
                        variant={groesse === g ? 'default' : 'outline'} className="min-w-12" onClick={() => setGroesse(g)}>
                  {g}
                </Button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">Im Preis ist ein T-Shirt enthalten.</p>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setDialog(null)}>Abbrechen</Button>
            <Button onClick={anmelden} disabled={speichert || !person || !groesse}>
              {speichert ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <UserPlus className="h-4 w-4 mr-1.5" />}
              Verbindlich anmelden
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Absage */}
      <Dialog open={!!absage} onOpenChange={(o) => !o && setAbsage(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Anmeldung absagen — {absage?.a?.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">
            {absage?.p?.anmeldung_offen
              ? 'Vor dem Anmeldeschluss: Die Gebühr wird zurückgenommen, sofern die Rechnung noch nicht festgeschrieben ist.'
              : 'Nach dem Anmeldeschluss: Die Gebühr bleibt fällig (verbindliche Anmeldung).'}
          </p>
          <div className="space-y-1.5 mt-2">
            <Label htmlFor="pa-grund">Grund (optional)</Label>
            <Input id="pa-grund" value={absage?.grund || ''} maxLength={200}
                   onChange={(e) => setAbsage((x) => ({ ...x, grund: e.target.value }))} />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setAbsage(null)}>Abbrechen</Button>
            <Button variant="destructive" onClick={absagenSenden} disabled={busyId === absage?.a?.id}>
              <XCircle className="h-4 w-4 mr-1.5" /> Absagen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
